import { buildWorkosAuthorizeUrl, createPkcePair, isElectron } from '@fluxter/shared';

const VERIFIER_STORAGE_KEY = 'fluxter.auth.pkce_verifier';
const MAGIC_EMAIL_STORAGE_KEY = 'fluxter.auth.magic_email';
const WORKOS_CLIENT_ID = import.meta.env.VITE_WORKOS_CLIENT_ID;

if (!WORKOS_CLIENT_ID) {
  throw new Error('VITE_WORKOS_CLIENT_ID is not set.');
}

/*
  Multi-org WorkOS accounts (where the authenticating user is a
  member of more than one Organization under our client) are
  handled SERVER-SIDE in `WorkosAuthService::authenticate` — when
  WorkOS returns `code: organization_selection_required` on the
  initial auth-code exchange, the API auto-resumes via the
  organization-selection grant scoped to the configured Fluxter
  org. The SPA doesn't pass `organization_id` here at authorize
  time because that param is for org-scoped SSO (SAML/OIDC), not
  social-auth providers like `GoogleOAuth` whose Connection lives
  at the WorkOS account level.
*/

const webRedirectUri = (): string => `${window.location.origin}/auth/callback`;

export interface SignInResult {
  code: string;
  codeVerifier: string;
  redirectUri: string;
}

interface ElectronBridge {
  signInWithWorkos: (params: {
    clientId: string;
    codeChallenge: string;
    provider?: string;
  }) => Promise<{ code: string; redirectUri: string }>;
}

const electron = (): ElectronBridge | null => {
  if (!isElectron()) return null;
  const bridge = (window as unknown as { fluxter?: Partial<ElectronBridge> }).fluxter;
  return bridge?.signInWithWorkos ? (bridge as ElectronBridge) : null;
};

/**
 * Web flow: builds the WorkOS authorize URL, stashes the PKCE verifier,
 * and navigates the page to WorkOS.
 *
 * Slice 1+2 own-UI: callers pass `provider` to skip the AuthKit
 * connection picker — `'GoogleOAuth'` is what we wire to the
 * "Continue with Google" button. Omitting `provider` falls through
 * to the AuthKit hosted picker (kept as a soft-cutover escape hatch
 * but no longer surfaced in the UI).
 */
export const beginWebSignIn = async (provider?: string): Promise<never> => {
  const { verifier, challenge } = await createPkcePair();
  sessionStorage.setItem(VERIFIER_STORAGE_KEY, verifier);

  const url = buildWorkosAuthorizeUrl({
    clientId: WORKOS_CLIENT_ID,
    redirectUri: webRedirectUri(),
    codeChallenge: challenge,
    provider,
  });

  window.location.assign(url);
  // window.location.assign is async-ish but the page will unload before this returns.
  return new Promise<never>(() => {});
};

/**
 * Desktop flow: asks the Electron main process to handle the loopback OAuth dance,
 * then returns the resulting `{ code, codeVerifier, redirectUri }` ready for the API.
 */
export const beginDesktopSignIn = async (provider?: string): Promise<SignInResult> => {
  const bridge = electron();
  if (!bridge) {
    throw new Error('Desktop bridge not available — are you running inside Electron?');
  }
  const { verifier, challenge } = await createPkcePair();
  const { code, redirectUri } = await bridge.signInWithWorkos({
    clientId: WORKOS_CLIENT_ID,
    codeChallenge: challenge,
    provider,
  });
  return { code, codeVerifier: verifier, redirectUri };
};

/**
 * Web callback: pulls the stashed verifier and the `?code=` from the URL.
 * Returns null if either is missing (caller should redirect back to /login).
 */
export const consumeWebCallback = (): SignInResult | null => {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const codeVerifier = sessionStorage.getItem(VERIFIER_STORAGE_KEY);
  sessionStorage.removeItem(VERIFIER_STORAGE_KEY);
  if (!code || !codeVerifier) return null;
  return { code, codeVerifier, redirectUri: webRedirectUri() };
};

/* -------------------------------------------------------------------------- */
/* Magic-auth helpers (Slice 1+2 own-UI)                                       */
/* -------------------------------------------------------------------------- */

/**
 * Stash the email used to request a magic-auth code so the next
 * page (`/auth/code-sent`) can read it back even after a hard
 * refresh. `sessionStorage` not `localStorage` — codes are
 * single-use and time-bound, no point persisting across windows.
 */
export const stashMagicAuthEmail = (email: string): void => {
  sessionStorage.setItem(MAGIC_EMAIL_STORAGE_KEY, email);
};

/** Read the stashed email; clears it on read so a back-navigation
 *  to `/login` doesn't quietly re-use a stale value. */
export const consumeMagicAuthEmail = (): string | null => {
  const email = sessionStorage.getItem(MAGIC_EMAIL_STORAGE_KEY);
  sessionStorage.removeItem(MAGIC_EMAIL_STORAGE_KEY);
  return email;
};

/** Read without clearing — used by `/auth/code-sent` on mount so a
 *  page refresh keeps the email visible for the user. */
export const peekMagicAuthEmail = (): string | null =>
  sessionStorage.getItem(MAGIC_EMAIL_STORAGE_KEY);
